import { Text } from '@/components/text';
import { Chart } from '@/components/ui/chart';
import {
  getEmissionsRadialChartConfig,
  getEmissionsRadialChartData,
} from '@/lib/models/emissions/utils/transform-chart-data';
import { RadialChartData } from '@/types';
import { FuelBurnUnitType } from '@/types/api-types';
import { formatEmissionsString } from '@/utils/format/emissions';
import { tv } from '@/utils/styles';

interface EmissionsRadialChartProps {
  title: string;
  unit?: FuelBurnUnitType;
  data: RadialChartData[];
  variant?: 'default' | 'large';
}

const EmissionsRadialChart = ({ title, unit, data, variant = 'default' }: EmissionsRadialChartProps) => {
  const { legend, legendItem, chartTitle, container, badge, smallText } = styles({ variant });

  const chartConfig = getEmissionsRadialChartConfig(data);
  const chartData = getEmissionsRadialChartData(data);

  return (
    <div>
      <Text variant="sm" className={chartTitle()}>
        {title}
      </Text>
      <div className={container()}>
        <Chart
          data={chartData}
          chartConfig={chartConfig}
          type="pie"
          settings={{
            pie: {
              dataKey: 'percent',
              nameKey: 'name',
              innerRadius: 20,
              outerRadius: 32,
              isAnimationActive: false,
            },
            tooltip: {
              hide: true,
            },
          }}
          className="size-16" // Match the outerRadius x 2
        />
        <div className={legend()}>
          {data.map((item) => {
            return (
              <div key={item.name} className={legendItem()}>
                <div className={badge()} style={{ background: item.color, color: item.textColor }}>
                  {item.percent === 0 ? '< 1' : item.percent}%
                </div>
                <Text variant="sm" className="m-0 leading-tight">
                  {item.name}
                  <small className={smallText()}>{formatEmissionsString(item.value, unit)}</small>
                </Text>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    chartTitle: 'mb-0 text-grey-500',
    container: 'mt-2 flex items-center gap-4',
    legend: 'flex flex-1 flex-col gap-1.5 text-sm',
    legendItem: 'grid grid-cols-[40px_1fr] items-center gap-1.5',
    badge: 'justify-self-start whitespace-nowrap rounded px-1 py-px',
    smallText: 'block text-xs text-grey-500',
  },
  variants: {
    variant: {
      default: null,
      large: {
        chartTitle: 'mb-3 text-baseSm text-grey-800',
      },
    },
  },
});

export { EmissionsRadialChart };
